import axios from 'src/utils/axios';
import { ADMIN_URL } from 'src/utils/runtimeConfig';
import { React, useEffect, useState, useCallback, Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import Spinner from 'src/components/spinner';
import { AskConfirm, AskFile, AskText, AskSelect } from 'src/components/questions';
import {
	Card,
	Container,
	Row,
	Col,
	Button,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
	UncontrolledDropdown,
	ButtonDropdown,
	ButtonGroup,
	UncontrolledButtonDropdown
} from 'reactstrap';
// import socket from 'src/utils/socket';
import AskDatetime from 'src/components/questions/AskDatetime';

// <input type="datetime-local"> wants LOCAL time as "YYYY-MM-DDTHH:mm" (toISOString() would be UTC and shift the time on every save).
const toLocalInput = (seconds) => {
	const d = new Date((seconds || Date.now() / 1000) * 1000);
	const p = (n) => String(n).padStart(2, '0');
	return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const showDate = (seconds) => (seconds ? new Date(seconds * 1000).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : 'Not set');

const ZIP_FINISHED = ['DONE', 'FAILED', 'NO IMAGES'];

const Event = () => {
	const { id: event_id } = useParams();
	const [event, setEvent] = useState(null);
	const [downloading, setDownloading] = useState(false);

	let getDetails = useCallback(async () => {
		try {
			var res = await axios.get(`/event/${event_id}`);
			setEvent(res.data);
		} catch (error) {
			console.error(error);
		}
	}, [event_id]);

	// Refresh quickly while a photo zip is being built so the percentage visibly moves, slowly otherwise.
	const zipRunning = !!event && event.slideshow_zip != null && !ZIP_FINISHED.includes(event.slideshow_zip_progress);
	const pollMs = zipRunning ? 2500 : 10000;
	useEffect(() => {
		getDetails();
		let interval = setInterval(getDetails, pollMs);
		return () => {
			clearInterval(interval);
		};
	}, [getDetails, pollMs]);

	const downloadImages = async () => {
		if (downloading) return;
		try {
			setDownloading(true);
			// A failed or empty earlier attempt is cleared first so the new one starts clean.
			if (event.slideshow_zip != null) await axios.post(`/s3/${event_id}/event-images/delete`);
			const result = await axios.post(`/s3/${event_id}/event-images`);
			if (result.data && result.data.status === 'NO_IMAGES') alert('There are no photos for this event yet.');
			await getDetails();
		} catch (error) {
			console.error(error);
			alert(`Could not start the download: ${error?.response?.data?.error || error.message}`);
		} finally {
			setDownloading(false);
		}
	};

	const deleteImages = async () => {
		if (downloading) return;
		try {
			setDownloading(true);
			const result = await axios.post(`/s3/${event_id}/event-images/delete`);
			console.log(result);
			await getDetails();
			setDownloading(false);
		} catch (error) {
			setDownloading(false);
			return error;
		}
	};

	const createGame = async (game_count) => {
		try {
			await axios.post('/game/create', { game_count, event_id });
			await getDetails();
		} catch (error) {
			return error;
		}
	};

	const renameGame = async (game_id, name) => {
		try {
			await axios.post(`/game/${game_id}/update`, { name });
			await getDetails();
		} catch (error) {
			return error;
		}
	};

	// Human readable reason from an API/AWS error (e.g. VcpuLimitExceeded, InsufficientInstanceCapacity).
	const reason = (error) => error?.response?.data?.message || error?.response?.data?.error || error?.message || 'unknown error';

	// Returns null on success, otherwise why it failed.
	const launchInstance = async (game_id) => {
		try {
			await axios.get(`/s3/launch-instance/${game_id}`);
			return null;
		} catch (error) {
			return reason(error);
		}
	};

	const launchOne = async (game_id) => {
		const failure = await launchInstance(game_id);
		await getDetails();
		if (failure) alert(`The bot could not be started: ${failure}`);
	};

	// Starts every bot that isn't running, a few at a time (instead of one every few seconds),
	// then reports anything that failed in one message.
	const launchAll = async () => {
		const queue = event.games.filter((g) => !g.instance).map((g) => g.game_id);
		const failures = [];
		const worker = async () => {
			while (queue.length) {
				const game_id = queue.shift();
				const failure = await launchInstance(game_id);
				if (failure) failures.push(failure);
				getDetails(); // show progress as bots appear
			}
		};
		const total = queue.length;
		await Promise.all(Array.from({ length: Math.min(5, total) }, worker));
		if (failures.length) {
			const unique = [...new Set(failures)].join('\n');
			alert(`${failures.length} of ${total} bots could not be started:\n\n${unique}`);
		}
	};

	const setSlideshowBranding = async (image, size) => {
		try {
			if (size === 'large') await axios.post(`/event/${event_id}/slideshow/brand-large`, { image });
			if (size === 'small') await axios.post(`/event/${event_id}/slideshow/brand-small`, { image });
			await getDetails();
		} catch (error) {
			console.error(error);
			return 'There was a server error.';
		}
	};

	const setEventDetail = async (key, value) => {
		try {
			let newEvent = { ...event };
			newEvent[key] = value;
			await axios.post(`/event/${event_id}/update`, newEvent);
			await getDetails();
		} catch (error) {
			return error;
		}
	};

	// (Automatic "join meeting" is switched off for now; the join_zoom / join_teams commands still exist in the bot.)

	const killProcess = (instanceID, processName) => {
		axios.post(`/s3/instance-command/${instanceID}`, {
			instanceID,
			command: 'kill',
			processName
		});
		// socket.send('bot_command', {
		// 	instanceID,
		// 	command: 'kill',
		// 	processName
		// });
	};

	// Terminates the machine directly in AWS. This works even when the bot never booted or its
	// program is stuck (the old 'terminate' command only worked if the bot was alive to receive it).
	// Returns null on success, otherwise why it failed.
	const terminateQuietly = async (instanceID) => {
		try {
			await axios.post(`/s3/terminate-instance/${instanceID}`);
			return null;
		} catch (error) {
			return reason(error);
		}
	};

	const terminate = async (instanceID) => {
		const failure = await terminateQuietly(instanceID);
		if (failure) alert(`Could not terminate ${instanceID}: ${failure}`);
		await getDetails();
	};

	const allTerminate = async () => {
		const running = event.games.filter((g) => g.instance);
		const failures = (await Promise.all(running.map((g) => terminateQuietly(g.instance.instance_id)))).filter(Boolean);
		await getDetails();
		if (failures.length) alert(`${failures.length} of ${running.length} bots could not be terminated:\n\n${[...new Set(failures)].join('\n')}`);
	};

	// Tells a bot to open a web page in Chrome. That is ALL it does: no Zoom/Teams automation and no screen
	// sharing - whoever takes over the bot with VNC does the sharing by hand.
	const openLink = (instanceID, url, fullscreen = false) =>
		axios.post(`/s3/instance-command/${instanceID}`, { instanceID, command: 'open_link', url, fullscreen });

	// The presenter screen for this team, fullscreen, ready to be shared from the bot.
	const showGame = (instanceID, game_id) => openLink(instanceID, `${ADMIN_URL}/present/${game_id}`, true);

	const allShowGame = () => {
		for (let i = 0; i < event.games.length; i++) {
			const g = event.games[i];
			if (g.instance) showGame(g.instance.instance_id, g.game_id);
		}
	};

	// The meeting (Zoom/Teams) web link, opened in the browser so it is already on screen when you connect.
	const hasMeetingLink = () => {
		if (/^https?:\/\//i.test(event.meeting_url || '')) return true;
		alert('Set a Meeting URL (starting with https://) for this event first.');
		return false;
	};
	const openMeetingLink = (instanceID) => {
		if (hasMeetingLink()) openLink(instanceID, event.meeting_url, false);
	};
	const allOpenMeetingLink = () => {
		if (!hasMeetingLink()) return;
		event.games.forEach((g) => {
			if (g.instance) openLink(g.instance.instance_id, event.meeting_url, false);
		});
	};

	// One-click remote control: downloads a .vnc file that opens straight into this bot.
	const downloadVnc = async (instance_id, name) => {
		try {
			const res = await axios.get(`/s3/vnc-file/${instance_id}`, { responseType: 'blob' });
			const url = URL.createObjectURL(res.data);
			const a = document.createElement('a');
			a.href = url;
			a.download = `${String(name || instance_id).replace(/[^\w .-]+/g, '_')}.vnc`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(url);
		} catch (error) {
			let message = error.message;
			try {
				message = JSON.parse(await error.response.data.text()).message || message;
			} catch (e) {}
			alert(`Could not create the VNC file: ${message}`);
		}
	};

	const GameEntry = ({ game }) => {
		const resetGame = async (gameId) => {
			console.log('reset ' + gameId);
			try {
				await axios.get(`/game/${gameId}/reset`);
			} catch (error) {
				console.error(error);
			}
		};
		return (
			<tr>
				<td className="td-team-name" data-game="$gid">
					{' '}
					{game.game_name} &nbsp;
					<AskText value={game.game_name} onSubmit={async (v) => await renameGame(game.game_id, v)} />
				</td>
				<td className="td-exp-players" data-expected={game.expected_players} data-game="$gid">
					-
				</td>
				<td className="td-points" data-game="$gid">
					{game.score ?? 0}
				</td>
				<td> - </td>
				<td data-game="$gid" className="td-jam-name text-center">
					{game.instance ? (
						<>
							{game.instance.instance_id}
							<br />
							{game.instance.instance_ip || 'Booting up'}
							{game.instance.instance_ip && (
								<button
									type="button"
									className="btn btn-outline-secondary btn-sm ml-2 py-0 px-1"
									title="Download a VNC file that connects straight to this bot"
									onClick={() => downloadVnc(game.instance.instance_id, game.game_name)}
								>
									<i className="fa fa-plug" />
								</button>
							)}
							<br />
							{game.instance.region}
							{game.instance.instance_ip && game.instance.seen_ago !== null && game.instance.seen_ago > 30 && (
								<>
									<br />
									<small className="text-danger" title="This bot has stopped reporting in. It is removed automatically after about 2 minutes.">
										<i className="fa fa-exclamation-triangle" /> No signal for {game.instance.seen_ago}s
									</small>
								</>
							)}
						</>
					) : (
						<>-None-</>
					)}
				</td>
				<td>
					<UncontrolledDropdown>
						<DropdownToggle caret>
							<i className="fas fa-robot mr-1"></i> Bot
						</DropdownToggle>
						<DropdownMenu>
							<DropdownItem header>Bot Controls</DropdownItem>
							{game.instance ? (
								<>
									{/* Automatic joining is switched off for now - join by hand over VNC */}
									<DropdownItem disabled>
										<i className="fa-fw far fa-play-circle"></i> Join Meeting
									</DropdownItem>
									<DropdownItem onClick={() => openMeetingLink(game.instance.instance_id)}>
										<i className="fa-fw fas fa-external-link-alt"></i> Open Meeting Link in Browser
									</DropdownItem>
									<DropdownItem onClick={() => killProcess(game.instance.instance_id, event.meeting_platform.toLowerCase())}>
										<i className="fa-fw fas fa-skull-crossbones"></i> Kill Meeting
									</DropdownItem>
									<DropdownItem divider />
									<DropdownItem onClick={() => showGame(game.instance.instance_id, game.game_id)}>
										<i className="fa-fw far fa-play-circle"></i> Show Game (open presenter)
									</DropdownItem>
									<DropdownItem onClick={() => killProcess(game.instance.instance_id, 'chrome')}>
										<i className="fa-fw fas fa-skull-crossbones"></i> Kill Game Presenter
									</DropdownItem>
									<DropdownItem divider />
									<DropdownItem onClick={() => terminate(game.instance.instance_id)}>
										<i className="fa-fw fas fa-skull-crossbones"></i> Terminate Bot
									</DropdownItem>
								</>
							) : (
								<DropdownItem onClick={() => launchOne(game.game_id)}>
									<i className="fa-fw far fa-play-circle"></i> START
								</DropdownItem>
							)}
						</DropdownMenu>
					</UncontrolledDropdown>

					<span className="dropdown">
						<button className="btn btn-outline-secondary btn-sm dropdown-toggle" type="button" id="actionDropdown$gid" data-toggle="dropdown">
							<i className="fas fa-gamepad mr-1"></i> Game
						</button>
						<div className="dropdown-menu dropdown-menu-right">
							<a href={`/game/${game.game_id}/progress`} className="dropdown-item">
								<i className="fa-fw fa fa-tasks"></i> View Team Progress
							</a>
							<div className="dropdown-divider"></div>
							<a href={`/present/${game.game_id}`} className="dropdown-item">
								<i className="fa-fw far fa-play-circle"></i> Open Presenter Screen
							</a>
							<a href="/play/$g[game_key]" className="dropdown-item disabled">
								<i className="fa-fw far fa-play-circle"></i> Open Play Screen
							</a>
							<div className="dropdown-divider"></div>
							<button
								data-game="$gid"
								className="dropdown-item resetGame"
								onClick={() => {
									resetGame(game.game_id);
								}}
							>
								<i className="fa-fw fas fa-skull-crossbones"></i> Reset Game
							</button>
						</div>
					</span>
				</td>
			</tr>
		);
	};

	if (!event) return <Spinner />;
	return (
		<Container>
			<h1>
				<i className="far fa-calendar-alt"></i> {event.event_name} - {event.activity_name}
				<Link to="/" className="btn btn-outline-orange float-right">
					<i className="fa fa-arrow-left"></i> Back to manage
				</Link>
			</h1>
			<hr />

			<Card>
				<Row>
					<Col md={12} lg={6}>
						<div style={{ maxWidth: '500px' }} className="mt-2">
							<table className="table table-bordered">
								<tbody>
									<tr>
										<th>Event Name</th>
										<td>
											{event.event_name} <AskText value={event.event_name} onSubmit={async (v) => await setEventDetail('event_name', v)} />
										</td>
									</tr>
									<tr>
										<th>Meeting Platform</th>
										<td>
											{event.meeting_platform}
											<AskSelect
												value={event.meeting_platform}
												values={[
													{ value: 'ZOOM', text: 'Zoom' },
													{ value: 'TEAMS', text: 'Teams' }
												]}
												onSubmit={async (v) => {
													await setEventDetail('meeting_platform', v);
												}}
												title="Platform"
												label="Meeting Platform"
											></AskSelect>
										</td>
									</tr>
									<tr>
										<th>Meeting URL</th>
										<td style={{ maxWidth: '40px', overflow: 'hidden' }}>
											{event.meeting_url} <AskText value={event.meeting_url} onSubmit={async (v) => await setEventDetail('meeting_url', v)} />
										</td>
									</tr>
									<tr>
										<th>Event Start</th>
										<td>
											{showDate(event.event_start)}
											<AskDatetime
												isSet={!!event.event_start}
												value={toLocalInput(event.event_start || event.event_created)}
												onSubmit={async (v) => {
													await setEventDetail('event_start', new Date(v).getTime() / 1000);
												}}
											/>
										</td>
									</tr>
									<tr>
										<th>Event Ends</th>
										<td>
											{showDate(event.event_end)}
											<AskDatetime
												isSet={!!event.event_end}
												value={toLocalInput(event.event_end || event.event_start || event.event_created)}
												onSubmit={async (v) => {
													await setEventDetail('event_end', new Date(v).getTime() / 1000);
												}}
											/>
										</td>
									</tr>
									<tr>
										<th>Slideshow Branding (Large)</th>
										<td>
											<h5>
												<AskFile onSubmit={({ url, file_id }) => setSlideshowBranding(file_id, 'large')} />
											</h5>
											{event.slideshow_brand_large && (
												<>
													<img style={{ maxWidth: '100px', maxHeight: '600px' }} src={event.slideshow_brand_large} alt="Large Branding"></img>
													<AskConfirm onSubmit={() => setSlideshowBranding(null, 'large')}>
														<Button>Remove Image</Button>
													</AskConfirm>
												</>
											)}
										</td>
									</tr>
									<tr>
										<th>Slideshow Branding (Small)</th>
										<td>
											<h5>
												<AskFile onSubmit={({ url, file_id }) => setSlideshowBranding(file_id, 'small')} />
											</h5>
											{event.slideshow_brand_small && (
												<>
													<img style={{ maxWidth: '100px', maxHeight: '600px' }} src={event.slideshow_brand_small} alt="Small Branding"></img>
													<AskConfirm onSubmit={() => setSlideshowBranding(null, 'small')}>
														<Button>Remove Image</Button>
													</AskConfirm>
												</>
											)}
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</Col>

					<Col md={12} lg={6}>
						<h2>Resources</h2>-<h2>Automation</h2>
						<AskConfirm
							description="Start All Bots?"
							onSubmit={async (res) => {
								await launchAll();
							}}
						>
							<button className="autoBTN btn btn-sm btn-outline-secondary mb-1">
								<i className="far fa-play-circle"></i> Start All Bots
							</button>
						</AskConfirm>
						<br />
						{/* Automatic joining is switched off for now - join by hand over VNC */}
						<button className="autoBTN btn btn-sm btn-outline-secondary mb-1" disabled title="Switched off for now: join the meeting by hand over VNC">
							<i className="fa fa-sign-in-alt"></i> All Join Meeting
						</button>
						<br />
						<AskConfirm
							description="Open the meeting link in the browser on all bots?"
							onSubmit={async (res) => {
								await allOpenMeetingLink();
							}}
						>
							<button className="autoBTN btn btn-sm btn-outline-secondary mb-1">
								<i className="fa fa-fw fas fa-external-link-alt"></i> All Open Meeting Link in Browser
							</button>
						</AskConfirm>
						<br />
						<AskConfirm
							description="Open the presenter screen on all bots? (It opens the page only - it does not share the screen.)"
							onSubmit={async (res) => {
								await allShowGame();
							}}
						>
							<button className="autoBTN btn btn-sm btn-outline-secondary mb-1">
								<i className="fa fa-fw far fa-play-circle"></i> All Show Game
							</button>
						</AskConfirm>
						<br />
						<AskConfirm
							description="Terminate All Bots?"
							onSubmit={async (res) => {
								await allTerminate();
							}}
						>
							<button className="autoBTN btn btn-sm btn-outline-danger mb-1">
								<i className="far fa-stop-circle"></i> Terminate All Bots
							</button>
						</AskConfirm>
						<br />
						<button
							className="autoBTN btn btn-sm btn-outline-secondary mb-1 disabled"
							hidden
							data-href="/manage/event_script.php?fiveMinsEvent=<?=$event['event_id']?>"
						>
							<i className="fa fa-clock"></i> Last Five Minutes
						</button>
						<br />
					</Col>

					<Col lg={12} id="gameDiv">
						<div id="gameInDiv">
							<AskText
								title="Create Games"
								label="How many games would you like to create ?"
								type="number"
								onSubmit={async (v) => await createGame(Number(v))}
							>
								<button id="btnGameCreate" className="float-right btn btn-outline-primary mb-1">
									<i className="fa fa-plus" aria-hidden="true"></i> Create Game
								</button>
							</AskText>
							{event?.games[0] && (
								<Link to={`/game/${event.games[0].game_id}/progress`}>
									<Button color="primary" outline className="float-right mx-1">
										<i className="fa fa-tasks" />
										&nbsp;View Progress
									</Button>
								</Link>
							)}
							<Link to={`/event/${event_id}/scoring`}>
								<Button color="primary" outline className="float-right mx-1">
									<i className="fa fa-images" />
									&nbsp;Score Images
								</Button>
							</Link>
							<Link to={`/slideshow/${event_id}`} target="_blank">
								<Button color="primary" outline className="float-right mx-1">
									<i className="fa fa-images" />
									&nbsp;Slideshow
								</Button>
							</Link>

							<ButtonGroup>
								<Button
									outline
									color={event.slideshow_zip == null || event.slideshow_zip_progress === 'FAILED' || event.slideshow_zip_progress === 'NO IMAGES' ? 'danger' : event.slideshow_zip_progress === 'DONE' ? 'success' : 'warning'}
									onClick={() => {
										if (event.slideshow_zip_progress === 'DONE') window.open(event.slideshow_zip_url, '_blank'); // the finished zip
										else if (!zipRunning) downloadImages(); // not started, failed or empty: (re)start
									}}
									disabled={zipRunning || downloading}
								>
									{downloading || zipRunning ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-images" />}
									&nbsp;
									{event.slideshow_zip == null
										? 'Start Images download'
										: event.slideshow_zip_progress === 'DONE'
										? 'Download ready'
										: event.slideshow_zip_progress === 'FAILED'
										? 'Failed - click to retry'
										: event.slideshow_zip_progress === 'NO IMAGES'
										? 'No photos yet - click to retry'
										: `Preparing photos ${event.slideshow_zip_progress || ''}`}
								</Button>
								<UncontrolledButtonDropdown>
									<DropdownToggle
										color={event.slideshow_zip == null ? 'danger' : event.slideshow_zip_progress === 'DONE' ? 'success' : 'warning'}
										outline
										caret
									/>
									<DropdownMenu right>
										<DropdownItem header>Actions</DropdownItem>
										<DropdownItem
											onClick={() => {
												deleteImages();
											}}
										>
											<i className="fa fa-fw fa-trash mr-2"></i>
											Delete
										</DropdownItem>
									</DropdownMenu>
								</UncontrolledButtonDropdown>
							</ButtonGroup>

							<table id="gameTable" style={{ backgroundColor: 'white' }} className="table table-striped">
								<thead>
									<tr>
										<th>Name</th>
										<th>Players</th>
										<th>Points</th>
										<th>State</th>
										<th className="text-center">Bot</th>
										<th>Actions</th>
									</tr>
								</thead>
								<tbody>
									{event.games
										.sort((a, b) => {
											let na = parseInt(a.score ?? 0);
											let nb = parseInt(b.score ?? 0);

											if (na < nb) return 1;
											if (na > nb) return -1;
											return 0;
										})
										.map((ga) => (
											// Called as a function (not <GameEntry/>) so rows are not torn down and rebuilt on every
											// refresh - that closed the open Bot/Game dropdown every few seconds.
											<Fragment key={ga.game_id}>{GameEntry({ game: ga })}</Fragment>
										))}
								</tbody>
							</table>
						</div>
					</Col>

					<Col lg={12}>
						<ul id="photoList" className="list-group"></ul>
					</Col>
				</Row>
			</Card>
		</Container>
	);
};

export default Event;
